import * as React from 'react';
import {
  Stack,
  P,
  IconButton,
  Icon,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Menu from '@mui/material/Menu';
import {
  TableVirtuoso,
  TableComponents,
  TableBodyProps,
  ScrollerProps,
  TableProps,
} from 'react-virtuoso';
import styles from './VirtualizedTable.module.css';

interface ColumnData {
  dataKey: string; // keyof Data;
  label: string;
  numeric?: boolean;
  width: number;
  required?: boolean;
  form?: (onClose: () => unknown) => React.ReactNode;
  fixed?: boolean;
  tooltip?: string;
}

interface VirtualizedTableProps {
  headerCells: ColumnData[];
  rowRenderer?: (index: number, row: unknown) => React.ReactNode;
  rows: Array<unknown>;
}

const Scroller = React.forwardRef<
  HTMLDivElement,
  ScrollerProps & { context: unknown }
>((props, ref) => <TableContainer component={Paper} {...props} ref={ref} />);
Scroller.displayName = 'Scroller';

const Body = React.forwardRef<
  HTMLTableSectionElement,
  TableBodyProps & { context: unknown }
>((props, ref) => <TableBody {...props} ref={ref} />);
Body.displayName = 'Body';

const TableComponent = React.forwardRef<
  HTMLTableElement,
  TableProps & { context: unknown }
>((props, ref) => (
  <Table
    {...props}
    ref={ref}
    sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
  />
));
TableComponent.displayName = 'TableComponent';

const TableHeadComponent = React.forwardRef<
  HTMLTableSectionElement,
  { context: unknown }
>((props, ref) => <TableHead {...props} ref={ref} />);
TableHeadComponent.displayName = 'TableHeadComponent';

const VirtuosoTableComponents: TableComponents = {
  Scroller,
  Table: TableComponent,
  TableHead: TableHeadComponent,
  TableRow: ({ item: _item, ...props }: { item: unknown }) => (
    <TableRow {...props} />
  ),
  TableBody: Body,
};

function HeaderCell(props: { column: ColumnData }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const headerContent = (
    <Stack direction="row" className="justify-between">
      <P color={props.column.required ? 'primary-inverse' : 'primary'}>
        {props.column.label}
      </P>
      {props.column.form && (
        <IconButton
          icon={<Icon.PlusSquare />}
          label="Edit column"
          onClick={handleClick}
          color="secondary"
          className="min-w-8 min-h-8 max-w-8 max-h-8"
        />
      )}
      {props.column.form && (
        <Menu
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorEl={anchorEl}
        >
          {props.column.form(handleClose)}
        </Menu>
      )}
    </Stack>
  );

  // Render the header cell content with Tooltip if tooltip is provided
  const cellContent = props.column.tooltip ? (
    <Tooltip
      content={<div className="max-w-xs">{props.column.tooltip}</div>}
      position="bottom"
      arrow
      className={styles.tableHeaderTooltip}
    >
      <span className="flex items-center">
        {headerContent}
        <Icon.InformationCircle className="ml-2 opacity-85" />
      </span>
    </Tooltip>
  ) : (
    headerContent
  );

  return (
    <TableCell
      variant="head"
      align={props.column.numeric || false ? 'right' : 'left'}
      style={{
        width: props.column.width,
        color: props.column.required ? 'white' : '#1D2433',
        backgroundColor: props.column.required ? '#1D2433' : '#E1E3E5',
        position: props.column.fixed ? 'sticky' : 'static',
        zIndex: props.column.fixed ? 2 : 1,
        left: props.column.fixed ? 0 : 'auto',
      }}
    >
      {cellContent}
    </TableCell>
  );
}

function FixedHeaderContent(props: { columns: ColumnData[] }) {
  return (
    <TableRow>
      {props.columns.map((column) => (
        <HeaderCell key={column.dataKey} column={column} />
      ))}
    </TableRow>
  );
}

export default function ReactVirtualizedTable(props: VirtualizedTableProps) {
  // Add/remove class to body when component mounts/unmounts
  // this is to fix the tooltip z-index issue
  React.useEffect(() => {
    document.body.classList.add('virtualized-table-active');

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('virtualized-table-active');
    };
  }, []);

  return (
    <Paper className={styles.tableContainer}>
      <TableVirtuoso
        data={props.rows}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() => (
          <FixedHeaderContent columns={props.headerCells} />
        )}
        itemContent={props.rowRenderer}
      />
    </Paper>
  );
}
